/**
 * Utility to calculate payment distribution breakdown based on Druyp business rules.
 */

// Helper to calculate date difference in months and remaining days
export function calculateDateDifference(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  const differenceMs = end.getTime() - start.getTime();
  const differenceDays = Math.floor(differenceMs / (1000 * 60 * 60 * 24)) + 1;
  
  const startDay = start.getDate();
  const endDay = end.getDate();
  
  let diffYear = end.getFullYear() - start.getFullYear();
  let diffMonth = end.getMonth() - start.getMonth();
  let months = diffYear * 12 + diffMonth;
  
  let totalDaysInLstMonth = new Date(
    end.getFullYear(),
    end.getMonth() + 1,
    0
  ).getDate();
  
  if (months < 0 || (months === 0 && endDay < startDay)) {
    months += 12;
  }
  
  let remainingDays = endDay - startDay + 1;
  if (remainingDays === totalDaysInLstMonth) {
    months++;
    remainingDays = 0;
  }
  
  if (remainingDays < 0) {
    months--;
    const tempDate = new Date(end);
    tempDate.setMonth(end.getMonth() - 1);
    remainingDays =
      new Date(tempDate.getFullYear(), tempDate.getMonth() + 1, 0).getDate() -
      startDay +
      endDay +
      1;
  }
  
  return {
    days: differenceDays,
    months: months,
    remainingDays: remainingDays,
    totalMonths: months + remainingDays / totalDaysInLstMonth,
  };
}

/**
 * Removes properties with a zero amount to generate a clean payload.
 */
export function cleanupEmptyDistributions(distribution) {
  const cleanDistribution = {};
  for (const [key, value] of Object.entries(distribution)) {
    if (value && value.amount > 0) {
      cleanDistribution[key] = value;
    }
  }
  return cleanDistribution;
}

/**
 * 1. buildInitialDistribution
 * Calculates one-time charges + first month rent.
 */
export function buildInitialDistribution({
  monthlyRent = 0,
  securityFee = 0,
  bookingChargesPercent = 0,
  cleaningTotal = 0,
  isProrated = false,
  proratedRent = 0,
  subscriptionType = "monthly",
  totalMonths = 1
}) {
  const baseRentVal = Number(monthlyRent) || 0;
  const rentVal = isProrated ? Number(proratedRent) : baseRentVal;
  
  const secFeeVal = Number(securityFee) || 0;
  const bookingPct = Number(bookingChargesPercent) || 0;
  const cleanVal = Number(cleaningTotal) || 0;
  
  const bookingFee = Number(((baseRentVal * bookingPct) / 100).toFixed(2));
  const cleaningCommission = Number((cleanVal * 0.10).toFixed(2));
  const cleaningCharges = Number((cleanVal * 0.90).toFixed(2));

  // If one time payment, charge full lease rent amount
  const finalRentAmount = subscriptionType === "monthly" ? rentVal : Number((baseRentVal * totalMonths).toFixed(2));

  const distribution = {
    monthly_rent: { amount: Number(finalRentAmount.toFixed(2)), receiver: "homeowner" },
    security_fee: { amount: Number(secFeeVal.toFixed(2)), receiver: "homeowner" },
    booking_fee: { amount: bookingFee, receiver: "druyp" },
    cleaning_charges: { amount: cleaningCharges, receiver: "cleaning_company" },
    cleaning_commission: { amount: cleaningCommission, receiver: "druyp" }
  };

  return cleanupEmptyDistributions(distribution);
}

/**
 * 2. buildMonthlyDistribution
 * Future schedules ONLY contain recurring monthly charges (rent).
 */
export function buildMonthlyDistribution(rentAmount) {
  const distribution = {
    monthly_rent: { amount: Number(Number(rentAmount).toFixed(2)), receiver: "homeowner" }
  };
  return cleanupEmptyDistributions(distribution);
}

/**
 * 3. buildMonthlySchedules
 * Iterates through the lease duration to build future schedule objects.
 */
export function buildMonthlySchedules({
  startDate,
  endDate,
  monthlyRent = 0,
  securityInsurance = 0
}) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const { months, remainingDays } = calculateDateDifference(start, end);
  
  const monthlySchedules = [];
  let currentDate = new Date(start);
  let monthsRemaining = months;
  
  const rentVal = Number(monthlyRent) || 0;
  const secInsVal = Number(securityInsurance) || 0;

  // Skip the first month in schedules because it's paid immediately!
  currentDate.setMonth(currentDate.getMonth() + 1);
  
  while (currentDate <= end) {
    const year = currentDate.getFullYear();
    const monthName = `${currentDate.toLocaleString("default", { month: "long" })}-${year}`;
    
    // Determine schedule rent amount (with proration if last partial month)
    const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    let daysToUse = monthsRemaining > 0 ? daysInMonth : remainingDays;
    monthsRemaining--;
    
    const installmentRentAmount = Number(((daysToUse / daysInMonth) * rentVal).toFixed(2));
    
    // Total amount charged to customer includes security insurance
    const totalAmount = Number((installmentRentAmount + secInsVal).toFixed(2));

    monthlySchedules.push({
      month: monthName,
      amount: totalAmount,
      distribution_data: buildMonthlyDistribution(installmentRentAmount)
    });
    
    currentDate.setMonth(currentDate.getMonth() + 1);
  }
  
  return monthlySchedules;
}

/**
 * 4. buildFinalPaymentPayload
 * Assembles the entire payload for the /api/payment-method/confirm API.
 */
export function buildFinalPaymentPayload({
  paymentIntent,
  paymentMethod,
  subscriptionType,
  housePrice,
  initialDistribution,
  monthlySchedules = []
}) {
  return {
    paymentIntent,
    paymentMethod,
    subscription_type: subscriptionType,
    housePrice,
    distribution_data: initialDistribution,
    monthlyPay: subscriptionType === "monthly" ? monthlySchedules : []
  };
}
